import { useCallback, useEffect } from 'react'
import { Routes, Route, useSearchParams, useLocation, useNavigate } from 'react-router-dom'
import { useAnonymousAuthToken, useAuthToken } from 'hooks/use-auth-token'
import Login, { isALoginStateParam } from 'components/Login'
import AuthenticatedContainer from 'components/AuthenticatedContainer'
import Home from 'components/Home'
import Admin from 'components/Admin'
import DeletionRequested from 'components/DeletionRequested'
import Organization from 'components/Organization'
import UserProfile from 'components/UserProfile'
import NotFound from 'components/NotFound'
import ErrorPage from 'components/Error'
import TwinNotFound from 'components/TwinNotFound'
import MyTwin from 'components/MyTwin'
import TwinProfileContainer from 'components/TwinProfile/TwinProfile'
import AnonymousConversation from 'components/Conversations/AnonymousConversation'
import Onboarding from 'components/FTUE/Onboarding'
import JelledBots from 'components/JelledBots'
import Fail from 'components/FTUE/Fail'
import ReactGA from 'analytics/analytics'
import './App.css'
import Gmail from 'components/Gmail'
import Slack from 'components/Slack'
import CompanyTrends from 'components/Insights/Company/CompanyTrends'
import Trends from 'components/Trends'
import LoadingSubscription from 'components/FTUE/LoadingSubscription'
import Unsubscribe from 'components/Unsubscribe'

const App = () => {
  const [authToken] = useAuthToken()
  const [anonymousAuthToken] = useAnonymousAuthToken()

  if (!authToken && !anonymousAuthToken) {
    // Routes available when logged out
    return (
      <Routes>
        <Route path="unsubscribe" element={<Unsubscribe />} />
        <Route path="auth/callback" element={<AuthCallback />} />
        <Route path="s/:shortUrl/*" element={<AnonymousConversation />} />
        <Route path="twin-not-found" element={<TwinNotFound />} />
        <Route path="twin-profile/:twinId/" element={<AnonymousConversation />} />
        <Route path="error" element={<ErrorPage />} />
        <Route path="*" element={<Login />} />
      </Routes>
    )
  }

  if (anonymousAuthToken) {
    return (
      <Routes>
        <Route path="auth/callback" element={<AuthCallback />} />
        <Route path="s/:shortUrl/*" element={<AnonymousConversation />} />
        <Route path="twin-profile/:twinId/" element={<AnonymousConversation />} />
        <Route path="twin-not-found" element={<TwinNotFound />} />
        <Route path="error" element={<ErrorPage />} />
        <Route path="*" element={<Login />} />
      </Routes>
    )
  }

  return (
    // Routes available when logged in as non anonymous user
    <Routes>
      <Route element={<AuthenticatedContainer />}>
        <Route path="unsubscribe" element={<Unsubscribe />} />
        <Route path="auth/callback" element={<AuthCallback />} />
        <Route index element={<Home />} />
        <Route path="/loading-subscription" element={<LoadingSubscription />} />
        <Route path="/trends" element={<Trends />} />
        <Route path="/trends/company/*" element={<CompanyTrends />} />
        <Route path="/deletion-requested" element={<DeletionRequested />} />
        <Route path="/onboarding" element={<Onboarding />} />
        <Route path="/onboarding/fail" element={<Fail />} />
        <Route path="/gmail/*" element={<Gmail />} />
        <Route path="/emails/*" element={<Gmail />} />
        <Route path="s/:shortUrl/*" element={<TwinProfileContainer />} />
        <Route path="twin-not-found" element={<TwinNotFound />} />
        <Route path="/twin-profile/*" element={<TwinProfileContainer />} />
        <Route path="/organization/*" element={<Organization />} />
        <Route path="/jelledbots/*" element={<JelledBots />} />
        <Route path="/slack" element={<Slack />} />
        <Route path="/admin/*" element={<Admin />} />
        <Route path="/my-twin/*" element={<MyTwin />} />
        <Route path="/profile/*" element={<UserProfile />} />
        <Route path="error" element={<ErrorPage />} />
        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  )
}

const AuthCallback = () => {
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')
  const navigate = useNavigate()
  const [, setToken] = useAuthToken()
  const [, setAnonymousAuthToken] = useAnonymousAuthToken()

  const parseStateRedirect = useCallback(() => {
    const state = searchParams.get('state')
    if (!state) {
      return '/'
    }

    try {
      const stateJson = JSON.parse(state)
      if (!isALoginStateParam(stateJson)) {
        throw new Error('Not a valid LoginStateParam')
      }

      window.sessionStorage.setItem('redirect_path', stateJson.redirect_path)
      return stateJson.redirect_path
    } catch {
      return '/'
    }
  }, [searchParams])

  const location = useLocation()

  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') {
      return
    }

    ReactGA.send({ hitType: 'pageview', page: location.pathname })
    // Track the pageview with react-ga
  }, [location])

  useEffect(() => {
    if (!token) {
      return
    }

    // when we log in as a non-anonymous user, we should clear the anonymous token
    setAnonymousAuthToken(null)
    setToken(token)
    navigate(parseStateRedirect(), { replace: true })
  }, [token, setToken, navigate, parseStateRedirect, setAnonymousAuthToken])

  return <p>Loading token for auth callback</p>
}

export default App
